// Getting started on Find (Craig, 22 Oct 2026; shortest path, fix plan PR 2, 29 Oct 2026): after the two onboarding
// screens and the intro, a new business does four things on Find, and the pages for the ones still to do are marked in
// red in the menu:
//   1. find its first firms: a Find prospects search (its "Start a campaign with these" fills in who to target), or
//      firms added by hand (Prospects, 2.1, 2.21)
//   2. check the campaign, already filled in from the search and the business's own words, and approve it (Find ›
//      Rules › Campaigns, 2.30)
//   3. connect an email account that can send: signed in and its email settings passing, so the mailbox is ready or
//      warming (Settings › Sending › Mailboxes, 0.7, 0.8). Signed in alone is not enough.
//   4. approve the first emails (Prospects › Drafts › To approve, 4.10). Nobody presses Run: once 1 to 3 are done,
//      the card asks for the first drafts itself (components/find/FindStart.tsx, outreach-agent draft: 2.20, 4.R, 4.2).
// It is done once a first message has actually gone (the campaign's sent count) and is never shown again after that.
// A firm enters its sequence when it is enrolled, before any draft is approved, so that is not the test.
import { useCallback, useEffect, useState } from "react";
import type { Crm } from "./crm";
import { campaigns, type Campaign } from "./campaigns";
import { sendingMailbox } from "./mailboxHealth";

/** Where Find opens after the intro, and where a business lands until its first email has gone. */
export const FIND_START_PATH = "/contacts/find";

export type FindStartKey = "firms" | "campaign" | "mailbox" | "approve";

export interface FindStartStep {
  key: FindStartKey;
  label: string;
  /** What to do there, or what Ogma is waiting for, in a few words. */
  hint: string;
  /** The pages for this step, marked in red while it is not done and its turn has come (the first is where its link goes). */
  paths: string[];
  done: boolean;
  /** Its turn has come: always, except approving, which waits until there are drafts. */
  due: boolean;
}

export interface FindStartFacts {
  /** A mailbox that can send (ready or warming), not merely signed in. */
  mailbox: boolean;
  /** A mailbox that is signed in but whose email settings haven't passed yet. */
  mailboxWaiting?: boolean;
  campaigns: Pick<Campaign, "status" | "search_id" | "counts">[];
}

const live = (c: Pick<Campaign, "status">) => c.status === "approved" || c.status === "paused";
const sum = (list: Pick<Campaign, "counts">[], f: (c: Campaign["counts"]) => number) => list.reduce((n, c) => n + f(c.counts), 0);

export function findStartSteps(f: FindStartFacts): FindStartStep[] {
  const running = f.campaigns.filter(live);
  const queued = sum(f.campaigns, (c) => c.planned + c.in_sequence + c.engaged);
  const drafts = sum(f.campaigns, (c) => c.drafts_waiting + c.hand_waiting);
  const sent = sum(f.campaigns, (c) => c.sent) > 0;
  return [
    {
      key: "firms",
      label: "Find your first firms",
      hint: "Type who you want to reach. Ogma ranks the best fits.",
      paths: ["/contacts/find", "/contacts/find/firm"],
      done: queued > 0 || f.campaigns.some((c) => !!c.search_id),
      due: true,
    },
    {
      key: "campaign",
      label: "Check your campaign and approve it",
      hint: "Who to write to and what you offer, filled in for you. Change anything, then approve.",
      paths: ["/campaigns/list"],
      done: running.length > 0,
      due: true,
    },
    {
      key: "mailbox",
      label: "Connect your email",
      hint: f.mailboxWaiting ? "Signed in. Waiting for your email settings to pass." : "Ogma writes from it, and replies come back to you.",
      paths: ["/settings/sending"],
      done: f.mailbox,
      due: true,
    },
    {
      key: "approve",
      label: "Approve your first emails",
      hint: "Ogma writes them once the steps above are done. Read them, change what you like, and approve.",
      paths: ["/campaigns/to-approve"],
      done: sent,
      due: drafts > 0,
    },
  ];
}

/** Getting started is over: a first message has gone. */
export const findStartDone = (steps: FindStartStep[]) => steps.every((s) => s.done);

/** The steps the first drafts wait for, still to do. */
export const runBlockers = (steps: FindStartStep[]) => steps.filter((s) => s.key !== "approve" && !s.done);

/** Everything is in place but no draft has been written yet: time to ask for the first drafts. */
export const needsFirstDrafts = (steps: FindStartStep[]) => {
  const approve = steps.find((s) => s.key === "approve");
  return runBlockers(steps).length === 0 && !!approve && !approve.done && !approve.due;
};

/** Paths marked in red: each step still to do whose turn has come. */
export function startPaths(steps: FindStartStep[]): Set<string> {
  if (findStartDone(steps)) return new Set();
  return new Set(steps.filter((s) => !s.done && s.due).flatMap((s) => s.paths));
}

/** Whether the business's first email has gone (Results unlocks then, 29 Oct 2026). */
export async function firstEmailSent(crm: Crm): Promise<boolean> {
  const camps = await campaigns(crm).catch(() => [] as Campaign[]);
  return sum(camps ?? [], (c) => c.sent) > 0;
}

export async function loadFindStart(crm: Crm): Promise<FindStartStep[]> {
  const [health, camps] = await Promise.all([crm.mailboxHealth().catch(() => null), campaigns(crm).catch(() => [] as Campaign[])]);
  const waiting = !sendingMailbox(health) && !!health?.mailboxes.some((m) => m.status === "setup");
  return findStartSteps({ mailbox: sendingMailbox(health) !== null, mailboxWaiting: waiting, campaigns: (camps ?? []).filter((c) => c.status !== "retired") });
}

// One business's steps, kept while the app is open: Home's Get started card reads them each time Home opens. Once
// getting started is over it is never read again.
const finished = new Set<string>();

/** The getting-started steps for this business, read again each time Home opens until they are all done. */
export function useFindStart(crm: Crm | null, tenant: string | undefined, active: boolean, path: string) {
  const [steps, setSteps] = useState<FindStartStep[] | null>(null);
  const [tick, setTick] = useState(0);
  const key = tenant ?? "";
  const on = active && !!crm && !!key && !finished.has(key);
  useEffect(() => {
    if (!on || !crm) return;
    let current = true;
    void loadFindStart(crm).then((s) => {
      if (!current) return;
      if (findStartDone(s)) finished.add(key);
      setSteps(s);
    });
    return () => {
      current = false;
    };
  }, [on, crm, key, path, tick]);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { steps: on ? steps : null, reload };
}

// Businesses whose first drafts the Get started card has asked for while the app is open, so a reload of the steps
// never asks twice (components/find/FindStart.tsx).
const askedFirstDrafts = new Set<string>();
export const firstDraftsAsked = (tenant: string) => askedFirstDrafts.has(tenant);
export function noteFirstDraftsAsked(tenant: string, asked: boolean): void {
  if (asked) askedFirstDrafts.add(tenant);
  else askedFirstDrafts.delete(tenant);
}

/** Forget what is known (a test starts again). */
export function forgetFindStart(): void {
  finished.clear();
  askedFirstDrafts.clear();
}
